import React, { useState, useEffect, useMemo, useRef } from 'react';
import './GeneradorFormulario.css';
import { obtenerPreguntasDeGrupo, obtenerTituloElemento } from '../utils/gruposSubgruposProcessor';
import FormField from './FormField';
import PreguntaErrorBoundary from './PreguntaErrorBoundary';
import { debugLog } from '../utils/debugLog';
import { LISTADO_PREGUNTAS_CONFIG, shouldFilterQuestion } from '../config/config-listado-preguntas';
import { determineQuestionStatus } from '../config/config-listado-preguntas';
import { shouldFilterGroupByName } from '../config/config-menu';
import evaluateMenuRelevant from '../utils/evaluateMenuRelevant';
import evaluateConstraint from '../utils/evaluateConstraint';
import FORM_CONFIG from '../config/formConfig';
import { sinDestinoDeGuardado, TEXTO_SIN_DESTINO } from '../utils/fieldSaveDestination';

/**
 * Componente que genera formularios dinámicos basados en la selección de grupos/subgrupos
 */
const GeneradorFormulario = ({ 
  elementoSeleccionado = null,
  onCambioRespuesta = () => {},
  respuestas = {},
  datosFormulario = {},
  modoVisualizacion = 'formulario', // 'formulario' | 'preview' | 'readonly' | 'debug'
  className = '',
  preguntaSeleccionada = null
}) => {
  const [respuestasLocales, setRespuestasLocales] = useState({});
  const [estadoValidacion, setEstadoValidacion] = useState({});
  // Nombres de campo ya inicializados (valor prellenado + validación inicial) en el grupo actual.
  // Evita re-ejecutar validarRespuesta/evaluateConstraint sobre preguntas ya procesadas
  // cada vez que el usuario escribe en CUALQUIER campo del formulario (ver useEffect de abajo).
  const camposInicializadosRef = useRef(new Set());

  // Capa 1 — estructura: qué preguntas pertenecen a este grupo/subgrupo, filtradas por
  // configuración estática (blacklist por nombre/tipo/grupo, config_actualizable). No depende
  // de las respuestas del usuario, solo del grupo seleccionado, así que solo se recalcula
  // al cambiar de grupo (no en cada tecla).
  const preguntasBaseDelGrupo = useMemo(() => {
    if (!elementoSeleccionado) return [];

    if (shouldFilterGroupByName(elementoSeleccionado)) {
      debugLog(`🚫 [FILTRO GRUPO] Ocultando grupo completo "${elementoSeleccionado.nombre}"`);
      return [];
    }

    const preguntasObtenidas = obtenerPreguntasDeGrupo(elementoSeleccionado);
    return preguntasObtenidas.filter(pregunta => !shouldFilterQuestion(pregunta, elementoSeleccionado));
  }, [elementoSeleccionado]);

  // Capa 2 — visibilidad por "relevant": sí depende de datosFormulario, porque un campo puede
  // aparecer/ocultarse en vivo según respuestas de OTROS campos. Es una evaluación barata (recorre
  // el AST ya transformado), sin validar constraints — por eso puede recalcularse en cada cambio
  // de datos sin costo relevante.
  const preguntasVisiblesPorRelevant = useMemo(() => {
    const aplicarRelevant = LISTADO_PREGUNTAS_CONFIG?.relevantFiltering?.applyRelevantFilters;
    if (!aplicarRelevant) return preguntasBaseDelGrupo;

    return preguntasBaseDelGrupo.filter(pregunta => {
      const tieneRelevant = pregunta.relevant_transformed !== undefined && pregunta.relevant_transformed !== null;
      if (!tieneRelevant) return true;
      try {
        return evaluateMenuRelevant(datosFormulario, pregunta.relevant_transformed);
      } catch (e) {
        return true;
      }
    });
  }, [preguntasBaseDelGrupo, datosFormulario]);

  // Capa 3 — si hay una pregunta seleccionada desde el buscador, mostrar solo esa
  const preguntasFormulario = useMemo(() => {
    if (!preguntaSeleccionada) return preguntasVisiblesPorRelevant;
    return preguntasVisiblesPorRelevant.filter(p => p?.name === preguntaSeleccionada?.name);
  }, [preguntasVisiblesPorRelevant, preguntaSeleccionada]);

  // Al cambiar de grupo, olvidar qué campos ya se inicializaron (son de otro grupo)
  useEffect(() => {
    camposInicializadosRef.current = new Set();
  }, [elementoSeleccionado]);

  // Prellenar valor inicial + validación SOLO para preguntas que aparecen por primera vez
  // (nuevas en el grupo, o recién visibles por "relevant"). El resto de las preguntas ya
  // inicializadas se saltan con un simple Set.has(): este efecto sí se re-ejecuta en cada
  // tecla (preguntasFormulario/respuestas cambian de referencia), pero ya no vuelve a correr
  // validarRespuesta/evaluateConstraint para las ~cientos de preguntas ya procesadas del grupo.
  useEffect(() => {
    if (!preguntasFormulario || preguntasFormulario.length === 0) return;

    const nuevosValores = {};
    const nuevosErrores = {};
    let huboPreguntasNuevas = false;

    preguntasFormulario.forEach(pregunta => {
      if (camposInicializadosRef.current.has(pregunta.name)) return;
      huboPreguntasNuevas = true;
      camposInicializadosRef.current.add(pregunta.name);

      const tieneRespuestaExterna = respuestas && Object.prototype.hasOwnProperty.call(respuestas, pregunta.name);
      const valorRespuestaExterna = tieneRespuestaExterna ? respuestas[pregunta.name] : undefined;

      let valorInicial;
      if (valorRespuestaExterna !== undefined && valorRespuestaExterna !== null && valorRespuestaExterna !== '') {
        valorInicial = valorRespuestaExterna;
      } else if (pregunta.config_rpt_valor !== null && pregunta.config_rpt_valor !== undefined && pregunta.config_rpt_valor !== '') {
        valorInicial = pregunta.config_rpt_valor;
      }

      if (valorInicial !== undefined) {
        nuevosValores[pregunta.name] = valorInicial;
      }
      nuevosErrores[pregunta.name] = validarRespuesta(pregunta, valorInicial ?? '');
    });

    if (!huboPreguntasNuevas) return;

    if (Object.keys(nuevosValores).length > 0) {
      setRespuestasLocales(prev => ({ ...prev, ...nuevosValores }));
    }
    setEstadoValidacion(prev => ({ ...prev, ...nuevosErrores }));

    debugLog(`📋 [PREGUNTAS] ${elementoSeleccionado?.tipo} "${elementoSeleccionado?.nombre}": ${Object.keys(nuevosErrores).length} pregunta(s) nueva(s) inicializada(s) de ${preguntasFormulario.length} visibles`);
  }, [preguntasFormulario, respuestas, elementoSeleccionado]);

  // Sincronizar respuestas externas con locales (p. ej. al revertir cambios desde el padre)
  useEffect(() => {
    // Solo sincronizar si hay respuestas externas y no están vacías
    if (respuestas && Object.keys(respuestas).length > 0) {
      setRespuestasLocales(prev => {
        const nuevasRespuestas = { ...prev, ...respuestas };
        return nuevasRespuestas;
      });
    }
  }, [respuestas]);

  const manejarCambioRespuesta = (nombreCampo, valor, pregunta) => {
    debugLog('🔄 [DEBUG GENERADOR] manejarCambioRespuesta interno ejecutado:', {
      nombreCampo,
      valor,
      pregunta: pregunta?.label || 'Sin label',
      tipo: pregunta?.type
    });
    
    try {
      debugLog('📊 [DEBUG GENERADOR] Paso 1: Actualizando respuestas locales...');
      // Actualizar respuestas locales
      setRespuestasLocales(prev => ({
        ...prev,
        [nombreCampo]: valor
      }));
      
      // Validar la respuesta
      const error = validarRespuesta(pregunta, valor);
      setEstadoValidacion(prev => ({
        ...prev,
        [nombreCampo]: error
      }));

      // Notificar al componente padre
      onCambioRespuesta(nombreCampo, valor, pregunta);
    } catch (error) {
      console.error('❌ [ERROR GENERADOR] Error en manejarCambioRespuesta:', error);
    }
  };

  const isEmptyValue = (v) => {
    if (v === null || v === undefined) return true;
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === 'object') {
      if (Object.prototype.hasOwnProperty.call(v, 'config_rpt_valor')) {
        const vv = v.config_rpt_valor;
        return vv === null || vv === undefined || String(vv).trim() === '';
      }
      return Object.keys(v).length === 0;
    }
    return String(v).trim() === '';
  };

  const validarRespuesta = (pregunta, valor) => {
    // Un campo sin config_tabla/config_columna nunca se guarda: no tiene sentido exigirlo como
    // requerido ni bloquear el formulario con un mensaje sobre un valor que no se va a persistir.
    if (sinDestinoDeGuardado(pregunta)) {
      return { valida: true, mensaje: '' };
    }

    if (pregunta.required && (!valor || valor.toString().trim() === '')) {
      return { valida: false, mensaje: 'Este campo es requerido' };
    }

    switch (pregunta.type) {
      case 'integer':
        if (valor && isNaN(parseInt(valor))) {
          return { valida: false, mensaje: 'Debe ser un número entero' };
        }
        // Validación adicional: no permitir números negativos por defecto
        // Solo aplicar si está habilitado en configuración y la pregunta no tiene constraint definida
        if (valor !== undefined && valor !== null && valor !== '') {
          const togglesType = FORM_CONFIG?.typeValidations || {};
          const hasConstraint = typeof pregunta?.constraint === 'string' && pregunta.constraint.trim().length > 0;
          if (!hasConstraint && togglesType.integerNonNegativeDefault) {
            const num = parseInt(valor);
            if (!isNaN(num) && num < 0) {
              return { valida: false, mensaje: 'No se permiten números negativos' };
            }
          }
        }
        break;
      case 'decimal':
        if (valor && isNaN(parseFloat(valor))) {
          return { valida: false, mensaje: 'Debe ser un número decimal' };
        }
        break;
      case 'email':
        if (valor && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valor)) {
          return { valida: false, mensaje: 'Debe ser un email válido' };
        }
        break;
      case 'date':
        if (valor && isNaN(Date.parse(valor))) {
          return { valida: false, mensaje: 'Debe ser una fecha válida' };
        }
        break;
    }
    // Validaciones por constraint (ODK-like)
    if (pregunta.constraint && typeof pregunta.constraint === 'string') {
      // Si el campo no tiene valor y no es requerido, no aplicar constraint
      if (isEmptyValue(valor)) {
        return { valida: true, mensaje: '' };
      }
      // Mezclar datos actuales con respuestas locales para evaluar con información fresca
      const datosParaEvaluar = {
        ...datosFormulario,
        ...respuestasLocales,
        [pregunta.name]: valor
      };
      const toggles = FORM_CONFIG?.constraintValidations || {};
      const ok = evaluateConstraint(pregunta.constraint, valor, datosParaEvaluar, pregunta, toggles);
      if (!ok) {
        const mensaje = pregunta.constraint_message || 'El valor no cumple la restricción definida';
        return { valida: false, mensaje };
      }
    }

    return { valida: true, mensaje: '' };
  };

  const renderizarCampo = (pregunta) => {
    const valor = respuestasLocales[pregunta.name] || '';
    const validacion = estadoValidacion[pregunta.name] || { valida: true, mensaje: '' };

    const manejarCambio = (nuevoValor) => {
      manejarCambioRespuesta(pregunta.name, nuevoValor, pregunta);
    };

    const manejarCambioArchivo = (e) => {
      const file = e.target.files[0];
      if (file) {
        const fileData = {
          file: file,
          name: file.name,
          size: file.size,
          type: file.type,
          isFile: true
        };
        manejarCambio(fileData);
      } else {
        manejarCambio(null);
      }
    };

    return (
      <FormField
        question={pregunta}
        value={valor}
        onChange={manejarCambio}
        error={!validacion.valida ? validacion.mensaje : null}
        modoVisualizacion={modoVisualizacion}
        handleFileChange={manejarCambioArchivo}
      />
    );
  };

  const validarCampo = (pregunta, valor) => {
    if (pregunta.required && (!valor || valor.toString().trim() === '')) {
      return { valida: false, mensaje: 'Este campo es requerido' };
    }
    return { valida: true, mensaje: '' };
  };

  const renderizarBadgesUsername = (pregunta) => {
    if (!LISTADO_PREGUNTAS_CONFIG.usernameFiltering.enabled || 
        !LISTADO_PREGUNTAS_CONFIG.usernameFiltering.showBadges) {
      return null;
    }

    const status = determineQuestionStatus(pregunta);
    
    if (!status || !status.statuses) {
      return null;
    }

    const hasUsernameStatus = status.statuses.some(s => 
      s.type === 'usernameVisible' || s.type === 'usernameHidden' || s.type === 'usernameNotEvaluated'
    );

    if (!hasUsernameStatus) {
      return null;
    }

    const badgeConfig = LISTADO_PREGUNTAS_CONFIG.usernameFiltering.badges;
    let badgeClass = 'username-badge';
    let badgeText = 'Username';
    let badgeColor = badgeConfig.colors.default;

    const usernameStatus = status.statuses.find(s => 
      s.type === 'usernameVisible' || s.type === 'usernameHidden' || s.type === 'usernameNotEvaluated'
    );

    if (usernameStatus.type === 'usernameVisible') {
      badgeClass += ' username-visible';
      badgeText = badgeConfig.text.visible;
      badgeColor = badgeConfig.colors.visible;
    } else if (usernameStatus.type === 'usernameHidden') {
      badgeClass += ' username-hidden';
      badgeText = badgeConfig.text.hidden;
      badgeColor = badgeConfig.colors.hidden;
    } else {
      badgeClass += ' username-not-evaluated';
      badgeText = badgeConfig.text.notEvaluated;
      badgeColor = badgeConfig.colors.notEvaluated;
    }

    return (
      <span 
        className={badgeClass}
        style={{ 
          backgroundColor: badgeColor,
          color: '#fff',
          padding: '2px 6px',
          borderRadius: '3px',
          fontSize: '10px',
          fontWeight: 'bold',
          marginLeft: '8px',
          display: 'inline-block'
        }}
        title={`Pregunta con ${(pregunta.relevant_transformed !== undefined ? 'relevant_transformed' : 'sin relevant_transformed')} que contiene username - Estado: ${usernameStatus.type}`}
      >
        {badgeText}
      </span>
    );
  };

  const renderizarPregunta = (pregunta, index) => {
    const validacion = estadoValidacion[pregunta.name] || { valida: true, mensaje: '' };
    
    return (
      <div key={pregunta.name || index} className={`pregunta-container ${pregunta.type}`}>
      <PreguntaErrorBoundary nombrePregunta={pregunta.label || pregunta.name}>
        <div className="pregunta-header">
          <label htmlFor={pregunta.name} className="pregunta-label">
            {obtenerTituloElemento(pregunta)}
            {(() => {
              const r = pregunta.required;
              const isReq = !(r === null || r === undefined || r === false || r === 0 || r === '0' || (typeof r === 'string' && r.trim().toLowerCase() === 'false'));
              return isReq ? <span className="campo-requerido">*</span> : null;
            })()}
            {sinDestinoDeGuardado(pregunta) && (
              <span
                className="pregunta-sin-destino-icono"
                title={FORM_CONFIG?.readonlyNote?.textSinDestino || TEXTO_SIN_DESTINO}
                aria-label={FORM_CONFIG?.readonlyNote?.textSinDestino || TEXTO_SIN_DESTINO}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 16,
                  height: 16,
                  marginLeft: 6,
                  borderRadius: '50%',
                  backgroundColor: '#9e9e9e',
                  color: '#fff',
                  fontSize: '11px',
                  fontStyle: 'italic',
                  fontWeight: 'bold',
                  cursor: 'help',
                  verticalAlign: 'middle'
                }}
              >
                i
              </span>
            )}
          </label>
        </div>
        {(() => {
          const rf = LISTADO_PREGUNTAS_CONFIG?.relevantFiltering;
          const tieneExpr = pregunta.relevant_transformed !== undefined && pregunta.relevant_transformed !== null;
          if (!rf?.showDebugConditionBadge) return null;

          const visiblePorRelevant = !tieneExpr ? true : evaluateMenuRelevant(datosFormulario, pregunta.relevant_transformed);
          const estado = !tieneExpr ? 'no-expression' : (visiblePorRelevant ? 'visible' : 'hidden');
          const maxLen = rf?.maxExpressionLength ?? 140;

          // Descripción cruda de la expresión
          const exprDesc = (() => {
            try { return JSON.stringify(pregunta.relevant_transformed); } catch { return ''; }
          })();

          // Extraer nombres de campos referenciados en la expresión relevant
          const referencedFields = (() => {
            const acc = new Set();
            const collect = (node) => {
              if (!node) return;
              if (Array.isArray(node)) { node.forEach(collect); return; }
              if (typeof node === 'object') {
                const type = node.type;
                if (type === 'function') {
                  if (typeof node.field === 'string') acc.add(node.field);
                  const args = Array.isArray(node.args) ? node.args : [];
                  args.forEach(a => { if (a && a.type === 'field' && typeof a.name === 'string') acc.add(a.name); });
                } else if (type === 'comparison') {
                  if (typeof node.field === 'string') acc.add(node.field);
                  const v = node.value;
                  if (v && v.type === 'field' && typeof v.name === 'string') acc.add(v.name);
                } else if (type === 'logical') {
                  const items = Array.isArray(node.items) ? node.items : (Array.isArray(node.expressions) ? node.expressions : []);
                  items.forEach(collect);
                } else {
                  if (typeof node.field === 'string') acc.add(node.field);
                }
              }
            };
            try { collect(pregunta.relevant_transformed); } catch {}
            return Array.from(acc);
          })();

          // Obtener valor visible (usando exclusivamente config_rpt_valor cuando exista) y soportar CSV
          const getDisplayValueForField = (name) => {
            const raw = datosFormulario ? datosFormulario[name] : undefined;
            if (raw === null || raw === undefined) return '';
            if (Array.isArray(raw)) {
              const parts = raw.map(v => {
                if (v && typeof v === 'object' && Object.prototype.hasOwnProperty.call(v, 'config_rpt_valor')) return String(v.config_rpt_valor);
                return String(v);
              }).flatMap(s => String(s).split(',').map(x => x.trim()).filter(Boolean));
              return parts.join(', ');
            }
            if (typeof raw === 'object') {
              const base = Object.prototype.hasOwnProperty.call(raw, 'config_rpt_valor') ? String(raw.config_rpt_valor) : String(raw);
              return base.split(',').map(x => x.trim()).filter(Boolean).join(', ');
            }
            return String(raw).split(',').map(x => x.trim()).filter(Boolean).join(', ');
          };

          // Construir tooltip
          const tooltip = rf?.showRawRelevantExpression
            ? exprDesc
            : (rf?.showDebugValues
                ? (() => {
                    try {
                      const current = tieneExpr ? getDisplayValueForField(pregunta.name) : undefined;
                      return `Estado: ${estado}${current !== undefined ? `\n${pregunta.name} = ${String(current)}` : ''}`;
                    } catch { return exprDesc; }
                  })()
                : exprDesc);

          const exprShown = exprDesc && exprDesc.length > maxLen ? `${exprDesc.slice(0, maxLen)}…` : exprDesc;
          // Aplicar colores de evaluación si está habilitado en la configuración
          const evalColorsEnabled = !!rf?.showEvaluationColors;
          const evalStateClass = evalColorsEnabled ? `relevant-eval-${estado}` : '';

          // Construir texto de campos referenciados y sus valores actuales
          const referencedDebugText = rf?.showDebugValues && referencedFields.length > 0
            ? referencedFields.map(fn => `${fn} = ${getDisplayValueForField(fn)}`).join(' | ')
            : '';

          return (
            <div className={`pregunta-debug ${evalStateClass}`}>
              <span className={`form-master-relevant-debug-badge-pregunta ${estado}`} title={tooltip}>
                <span className="state-dot" />
                <span className="expr">{exprShown || (tieneExpr ? '(expresión)' : '—')}</span>
              </span>
              {referencedDebugText && (
                <span className="debug-fields" style={{ marginLeft: 8, fontSize: '10px', color: '#555' }}>{referencedDebugText}</span>
              )}
            </div>
          );
        })()}
        
        {pregunta.hint && (
          <div className="pregunta-hint">
            {pregunta.hint}
          </div>
        )}
        
        <div className="pregunta-campo">
          {renderizarCampo(pregunta)}
          {!validacion.valida && (
            <div className="mensaje-error">
              {validacion.mensaje}
            </div>
          )}
        </div>
      </PreguntaErrorBoundary>
      </div>
    );
  };

  const tituloElemento = obtenerTituloElemento(elementoSeleccionado);

  return (
    <div className={`generador-formulario ${className}`}>
      {modoVisualizacion !== 'debug' && (
        <div className="formulario-header">
          <h3>{tituloElemento}</h3>
        </div>
      )}

      <div className="formulario-contenido">
        {preguntasFormulario.length === 0 ? (
          <div className="mensaje-sin-preguntas">
            <p>No hay preguntas disponibles para este elemento.</p>
          </div>
        ) : (
          preguntasFormulario.map((pregunta, index) => renderizarPregunta(pregunta, index))
        )}
      </div>
    </div>
  );
};

export default GeneradorFormulario;